import { toast } from 'react-toastify';

import { simulateContract, writeContract } from '@wagmi/core';

import type { Address } from 'viem';

import type { AsyncTransactionResult } from '@api';

// eslint-disable-next-line deprecate/import
import { wagmiConfig, type WagmiConfigChain } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { ERC20__factory } from '@shared/config/contracts/abi-typed';
import { namedWeb3Errors } from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

type Props = {
  amount: number | string;
  chainId: WagmiConfigChain['id'];
  spenderAddress: Address;
  tokenAddress: Address;
  tokenDecimals: number;
};

export const useApprove = () => {
  const { connectWallet } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const approve = async (props: Props): AsyncTransactionResult => {
    const { amount, chainId, spenderAddress, tokenAddress, tokenDecimals } = props;
    const toastId = Date.now();

    try {
      const { request } = await simulateContract(wagmiConfig, {
        abi: ERC20__factory.abi,
        address: tokenAddress,
        functionName: 'approve',
        chainId,
        args: [spenderAddress, numberToWei(amount, tokenDecimals).toBigInt()],
      });

      const { walletType, userWallet } = getUserWalletType();

      return (await Promise.race([
        writeContract(wagmiConfig, request),
        launchAsyncToast({
          walletType,
          address: userWallet.attributes.address,
          toastId,
          title: 'Approving transaction...',
        }),
      ])) as Awaited<AsyncTransactionResult>;
    } catch (error) {
      const normalizedError = normalizeError(error);

      const reconnectAndRetry = async () => {
        const { walletType, userWallet } = getUserWalletType();

        await connectWallet({
          walletType,
          targetAddress: userWallet.attributes.address,
        });

        return approve(props);
      };

      if (normalizedError.message === namedWeb3Errors.CONNECTOR_NOT_FOUND) {
        clearSavedConnectionDetails();
        return await reconnectAndRetry();
      }

      if (normalizedError.message === namedWeb3Errors.RETRY) {
        return await approve(props);
      }

      throw error;
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { approve };
};
